import { OccupationForm, OccupationSeries } from './models/occupation.model';

const HOUR_MS = 3600_000;

/** Largest capacity still legible as one lane per unit in the slot ladder. */
const MAX_LADDER_LANES = 12;

/**
 * The time span every form shares, so the three stay aligned when the user cycles
 * between them. Falls back to a one-hour window when there is nothing to plot, because
 * a zero span would collapse every mark onto a single pixel column.
 *
 * `include` widens the span to cover an interval that is drawn but not plotted, such as
 * the marked stopover window.
 */
export function seriesBounds(
  series: readonly OccupationSeries[],
  include?: readonly [number, number]
): {
  t0: number;
  t1: number;
} {
  let t0 = Number.POSITIVE_INFINITY;
  let t1 = Number.NEGATIVE_INFINITY;

  for (const s of series) {
    for (const step of s.steps) {
      if (step.fromMs < t0) t0 = step.fromMs;
      if (step.toMs > t1) t1 = step.toMs;
    }
  }

  if (include) {
    if (include[0] < t0) t0 = include[0];
    if (include[1] > t1) t1 = include[1];
  }

  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) {
    const base = Number.isFinite(t0) ? t0 : 0;
    return { t0: base, t1: base + HOUR_MS };
  }

  return { t0, t1 };
}

export function makeTimeScale(
  t0: number,
  t1: number,
  left: number,
  width: number
): (ms: number) => number {
  const span = t1 - t0;
  if (span <= 0) return () => left;
  return (ms: number) => left + ((ms - t0) / span) * width;
}

/** Whole-hour ticks inside the bounds, thinned to at most `maxTicks`. */
export function hourTicks(t0: number, t1: number, maxTicks: number): number[] {
  if (maxTicks <= 0 || t1 <= t0) return [];

  const first = Math.ceil(t0 / HOUR_MS) * HOUR_MS;
  const all: number[] = [];
  for (let t = first; t <= t1; t += HOUR_MS) all.push(t);

  if (all.length <= maxTicks) return all;

  const stride = Math.ceil(all.length / maxTicks);
  return all.filter((_, i) => i % stride === 0).slice(0, maxTicks);
}

/**
 * Clock label for a tick or a step edge, in the viewer's own zone. The timeline and the
 * step popup render local times, so a UTC label here would contradict the step times
 * shown next to it for every reader outside UTC.
 */
export function formatHourLabel(ms: number): string {
  const at = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/**
 * The ladder draws one lane per unit of capacity, so it is only meaningful for a small
 * whole number. Capacity is an f64: 2.5 bays or 500 m² of floor cannot be lanes.
 */
export function canLadder(capacity: number | null): boolean {
  return (
    capacity !== null &&
    Number.isInteger(capacity) &&
    capacity > 0 &&
    capacity <= MAX_LADDER_LANES
  );
}

export function supportsLadder(series: OccupationSeries): boolean {
  return canLadder(series.capacity);
}

/** What a panel actually renders, which is not always the form that was asked for. */
export type RenderedForm = OccupationForm | 'unavailable';

/**
 * Resolves the form actually rendered.
 *
 * A capacity that is a number but not a small whole one degrades to the area form rather
 * than disappearing from the cycle: there is still a limit to draw against, it just cannot
 * be lanes. A capacity of *none* is a different thing — there is no ceiling to divide into
 * slots at all — and substituting a step area there answers a question nobody asked, so
 * the panel says so instead.
 */
export function effectiveForm(
  requested: OccupationForm,
  series: OccupationSeries
): RenderedForm {
  if (requested !== 'ladder') return requested;
  if (series.capacity === null) return 'unavailable';
  return supportsLadder(series) ? 'ladder' : 'area';
}
